import { MinusCircleOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAssignConstructionSite,
  useUnassignBeaconsFromOrganization,
} from "../../api";
import { DataRefreshContext } from "../../contexts/dataRefreshContext";
import { Badge } from "../Badge";

interface UnassignBeaconsButtonSiteProps {
  constructionSiteId: string;
  selectedBeaconIds: string[];
  siteBeacons: string[];
  onClearSelectedBeaconIds(): void;
}

export function UnassignBeaconsButtonSite({
  constructionSiteId,
  selectedBeaconIds,
  siteBeacons,
  onClearSelectedBeaconIds,
}: UnassignBeaconsButtonSiteProps) {
  const { t } = useTranslation();

  const [isUnassigning, setIsUnassigning] = useState(false);

  const assignBeaconsToConstructionSite = useAssignConstructionSite();

  const dataRefreshContext = useContext(DataRefreshContext);

  const beaconsToKeep = siteBeacons?.filter(
    (beaconId) => !selectedBeaconIds.includes(beaconId)
  );

  const error = () => {
    Modal.error({
      title: t("translations:error-save"),
    });
  };

  const onUnassignBeaconsFromSite = async () => {
    try {
      setIsUnassigning(true);
      await assignBeaconsToConstructionSite({
        constructionSiteId: constructionSiteId,
        beaconIds: beaconsToKeep,
      });
      dataRefreshContext.refresh();
      onClearSelectedBeaconIds();
    } catch {
      error();
      console.error("Failed to unassign beacons from site");
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <>
      <Badge count={siteBeacons.length !== 1 ? selectedBeaconIds.length : null}>
        <Button
          danger
          disabled={selectedBeaconIds.length === 0 || siteBeacons.length === 1}
          loading={isUnassigning}
          onClick={onUnassignBeaconsFromSite}
          title={t("construction-site:unassign-beacons", {
            count: selectedBeaconIds.length,
          })}
        >
          <MinusCircleOutlined />
          {t("construction-site:unassign-beacons", {
            count: selectedBeaconIds.length,
          })}
        </Button>
      </Badge>
    </>
  );
}

interface UnassignBeaconsButtonOrganizationProps {
  organizationId: string;
  selectedBeaconIds: string[];
  onClearSelectedBeaconIds(): void;
}

export function UnassignBeaconsButtonOrganization({
  organizationId,
  selectedBeaconIds,
  onClearSelectedBeaconIds,
}: UnassignBeaconsButtonOrganizationProps) {
  const [isUnassigning, setIsUnassigning] = useState(false);
  const unassignBeaconsFromOrganization = useUnassignBeaconsFromOrganization();
  const { t } = useTranslation();
  const dataRefreshContext = useContext(DataRefreshContext);

  const onUnassignBeaconsFromOrganization = async () => {
    try {
      setIsUnassigning(true);
      await unassignBeaconsFromOrganization({
        beaconIds: selectedBeaconIds,
        organizationId,
      });
      onClearSelectedBeaconIds();
      dataRefreshContext.refresh();
    } catch {
      console.error("Failed to unassign beacons from organization");
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <>
      <Badge count={selectedBeaconIds.length}>
        <Button
          danger
          disabled={selectedBeaconIds.length === 0}
          loading={isUnassigning}
          onClick={onUnassignBeaconsFromOrganization}
          title={t("organization:unassign-beacons-message", {
            count: selectedBeaconIds.length,
          })}
        >
          <MinusCircleOutlined />
          {t("organization:unassign-beacons", {
            count: selectedBeaconIds.length,
          })}
        </Button>
      </Badge>
    </>
  );
}
